import { api } from "./client";
import type {
  User, LoginResponse, RoleType, Language, HotelProperty, ManagementCompany,
  Brand, Call, CallType, ServiceLevel, CallStatus, CallHistory, CallImportSummary,
  CallSummary,
  Order, OrderStatus, PaymentType, Survey, SurveyResponse, CallSurveyInfo, ContractSpec,
  ExistingSurveyResponse,
  Announcement, SystemVariable, ShopperCallSummary, ServiceLevelReport,
  CallerFeeSummary, CallerFeeByType, HMCMonthlySummaryRow, ScheduledVsCompletedRow,
  CallStatusByMonthRow, CompletedByHMCMonthRow, CompletedByShopperMonthRow,
  DuplicateScheduledRow, PurchaseOrderIn, PurchaseOrderOut, ExceptionalCallOut,
  BusinessIntelligenceOut, ManagementDashboardOut,
  PaginatedResponse,
} from "../types";

// === Auth ===
export const auth = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>("/accounts/login", { email, password }),
  logout: () => api.post<{ message: string }>("/accounts/logout"),
  me: () => api.get<User>("/accounts/me"),
};

// === Users ===
export const users = {
  list: (params?: string) =>
    api.get<PaginatedResponse<User>>(`/accounts/users${params ? `?${params}` : ""}`),
  get: (id: number) => api.get<User>(`/accounts/users/${id}`),
  create: (data: Partial<User> & { password: string }) =>
    api.post<User>("/accounts/users", data),
  update: (id: number, data: Partial<User>) =>
    api.patch<User>(`/accounts/users/${id}`, data),
  delete: (id: number) => api.delete<{ message: string }>(`/accounts/users/${id}`),
  changePassword: (id: number, oldPassword: string, newPassword: string) =>
    api.post<{ message: string }>(`/accounts/users/${id}/change-password`, {
      old_password: oldPassword, new_password: newPassword,
    }),
  resetLogin: (id: number) =>
    api.post<{ message: string }>(`/accounts/users/${id}/reset-login`),
  roles: () => api.get<RoleType[]>("/accounts/roles"),
  languages: () => api.get<Language[]>("/accounts/languages"),
};

// === Roles ===
export const roles = {
  list: () => api.get<RoleType[]>("/accounts/roles"),
  create: (data: { name: string; description?: string; abbreviation?: string; role_code?: string }) =>
    api.post<RoleType>("/accounts/roles", data),
  update: (id: number, data: { name?: string; description?: string; abbreviation?: string; role_code?: string }) =>
    api.patch<RoleType>(`/accounts/roles/${id}`, data),
  delete: (id: number) => api.delete<{ message: string }>(`/accounts/roles/${id}`),
};

// === Shoppers ===
export const shoppers = {
  list: (params?: string) =>
    api.get<PaginatedResponse<User>>(`/accounts/shoppers${params ? `?${params}` : ""}`),
  getAllocations: (id: number, year?: number) =>
    api.get<{ id: number; year: number; month: number; allocation: number }[]>(
      `/accounts/shoppers/${id}/allocations${year ? `?year=${year}` : ""}`
    ),
  setAllocation: (id: number, year: number, month: number, allocation: number) =>
    api.post(`/accounts/shoppers/${id}/allocations`, { year, month, allocation }),
  getIdentities: (id: number) =>
    api.get<{ id: number; first_name: string; last_name: string; email: string; phone: string; company: string }[]>(
      `/accounts/shoppers/${id}/identities`
    ),
  createIdentity: (id: number, data: { first_name: string; last_name: string; email?: string; phone?: string; company?: string }) =>
    api.post(`/accounts/shoppers/${id}/identities`, data),
};

// === Hotels ===
export const hotels = {
  brands: () => api.get<Brand[]>("/hotels/brands"),
  managementCompanies: {
    list: (params?: string) =>
      api.get<PaginatedResponse<ManagementCompany>>(`/hotels/management-companies${params ? `?${params}` : ""}`),
    get: (id: number) => api.get<ManagementCompany>(`/hotels/management-companies/${id}`),
    create: (data: Partial<ManagementCompany>) =>
      api.post<ManagementCompany>("/hotels/management-companies", data),
    update: (id: number, data: Partial<ManagementCompany>) =>
      api.patch<ManagementCompany>(`/hotels/management-companies/${id}`, data),
    delete: (id: number) => api.delete<{ message: string }>(`/hotels/management-companies/${id}`),
  },
  properties: {
    list: (params?: string) =>
      api.get<PaginatedResponse<HotelProperty>>(`/hotels/properties${params ? `?${params}` : ""}`),
    get: (id: number) => api.get<HotelProperty>(`/hotels/properties/${id}`),
    create: (data: Partial<HotelProperty>) =>
      api.post<HotelProperty>("/hotels/properties", data),
    update: (id: number, data: Partial<HotelProperty>) =>
      api.patch<HotelProperty>(`/hotels/properties/${id}`, data),
    delete: (id: number) => api.delete<{ message: string }>(`/hotels/properties/${id}`),
  },
  contractSpecs: {
    list: (mcId?: number) =>
      api.get<ContractSpec[]>(`/hotels/contract-specs${mcId ? `?mc_id=${mcId}` : ""}`),
    create: (data: Partial<ContractSpec> & { management_company_id: number }) =>
      api.post<ContractSpec>("/hotels/contract-specs", data),
    update: (id: number, data: Partial<ContractSpec>) =>
      api.patch<ContractSpec>(`/hotels/contract-specs/${id}`, data),
  },
};

// === Calls ===
export const calls = {
  types: () => api.get<CallType[]>("/calls/types"),
  serviceLevels: () => api.get<ServiceLevel[]>("/calls/service-levels"),
  statuses: () => api.get<CallStatus[]>("/calls/statuses"),
  rates: () => api.get<any[]>("/calls/rates"),
  list: (params?: string) =>
    api.get<PaginatedResponse<Call>>(`/calls/${params ? `?${params}` : ""}`),
  summary: (params?: string) =>
    api.get<CallSummary>(`/calls/summary${params ? `?${params}` : ""}`),
  get: (id: number) => api.get<Call>(`/calls/${id}`),
  create: (data: Partial<Call>) => api.post<Call>("/calls/", data),
  update: (id: number, data: Partial<Call>) => api.patch<Call>(`/calls/${id}`, data),
  archive: (id: number) => api.delete<{ message: string }>(`/calls/${id}`),
  reset: (id: number) => api.post<Call>(`/calls/${id}/reset`),
  override: (id: number, data: Partial<Call>) => api.post<Call>(`/calls/${id}/override`, data),
  uploadAudio: (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<Call>(`/calls/${id}/upload-audio`, fd);
  },
  importPreview: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<CallImportSummary>("/calls/import-call-details/preview", fd);
  },
  importCommit: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<CallImportSummary>("/calls/import-call-details/commit", fd);
  },
  history: (id: number) => api.get<CallHistory[]>(`/calls/${id}/history`),
};

// === Orders ===
export const orders = {
  statuses: () => api.get<OrderStatus[]>("/orders/statuses"),
  paymentTypes: () => api.get<PaymentType[]>("/orders/payment-types"),
  list: (params?: string) =>
    api.get<PaginatedResponse<Order>>(`/orders/${params ? `?${params}` : ""}`),
  get: (id: number) => api.get<Order>(`/orders/${id}`),
  create: (data: Partial<Order>) => api.post<Order>("/orders/", data),
  update: (id: number, data: Partial<Order>) => api.patch<Order>(`/orders/${id}`, data),
  delete: (id: number) => api.delete<{ message: string }>(`/orders/${id}`),
  purchase: (data: PurchaseOrderIn) => api.post<PurchaseOrderOut>("/orders/purchase", data),
};

// === Surveys ===
export const surveys = {
  templates: (includeInactive?: boolean) =>
    api.get<{ id: number; name: string; is_active: boolean }[]>(
      `/surveys/templates${includeInactive ? "?include_inactive=true" : ""}`
    ),
  getTemplate: (id: number) => api.get<Survey>(`/surveys/templates/${id}`),
  createTemplate: (data: { name: string; description?: string }) =>
    api.post<{ id: number; name: string; is_active: boolean }>("/surveys/templates", data),
  updateTemplate: (id: number, data: { name?: string; description?: string; is_active?: boolean }) =>
    api.patch<{ id: number; name: string; is_active: boolean }>(`/surveys/templates/${id}`, data),
  deleteTemplate: (id: number) =>
    api.delete<{ message: string }>(`/surveys/templates/${id}`),
  cloneTemplate: (id: number, name?: string) =>
    api.post<{ id: number; name: string; is_active: boolean }>(`/surveys/templates/${id}/clone`, { name: name || "" }),
  templateHMCs: (id: number) =>
    api.get<{ contract_spec_id: number; management_company_id: number; management_company_name: string }[]>(`/surveys/templates/${id}/hmcs`),
  assignHMC: (surveyId: number, mcId: number) =>
    api.post<{ message: string }>(`/surveys/templates/${surveyId}/assign-hmc`, { management_company_id: mcId }),
  unassignHMC: (surveyId: number, mcId: number) =>
    api.post<{ message: string }>(`/surveys/templates/${surveyId}/unassign-hmc`, { management_company_id: mcId }),
  // Categories
  createCategory: (data: { survey_id: number; name: string; order?: number }) =>
    api.post<any>("/surveys/categories", data),
  updateCategory: (id: number, data: { name?: string; order?: number; is_active?: boolean }) =>
    api.patch<any>(`/surveys/categories/${id}`, data),
  deleteCategory: (id: number) =>
    api.delete<{ message: string }>(`/surveys/categories/${id}`),
  // Questions
  getQuestion: (id: number) => api.get<any>(`/surveys/questions/${id}`),
  questionsByCategory: (catId: number) => api.get<any[]>(`/surveys/questions-by-category/${catId}`),
  createQuestion: (data: { category_id: number; text: string; order?: number; max_score?: number; choice_point_value_ids?: number[] }) =>
    api.post<any>("/surveys/questions", data),
  updateQuestion: (id: number, data: { text?: string; order?: number; max_score?: number; is_active?: boolean; choice_point_value_ids?: number[] }) =>
    api.patch<any>(`/surveys/questions/${id}`, data),
  deleteQuestion: (id: number) =>
    api.delete<{ message: string }>(`/surveys/questions/${id}`),
  // Point Values
  pointValues: () => api.get<{ id: number; name: string; value: number | null; used_in_surveys: string[] }[]>("/surveys/point-values"),
  createPointValue: (data: { name: string; value?: number }) =>
    api.post<{ id: number; name: string; value: number | null }>("/surveys/point-values", data),
  updatePointValue: (id: number, data: { name: string; value?: number }) =>
    api.patch<{ id: number; name: string; value: number | null }>(`/surveys/point-values/${id}`, data),
  deletePointValue: (id: number) =>
    api.delete<{ message: string }>(`/surveys/point-values/${id}`),
  // Survey resolution & responses
  forCall: (callId: number) => api.get<CallSurveyInfo>(`/surveys/for-call/${callId}`),
  responses: (params?: string) =>
    api.get<PaginatedResponse<SurveyResponse>>(`/surveys/responses${params ? `?${params}` : ""}`),
  existingResponse: (callId: number) =>
    api.get<ExistingSurveyResponse>(`/surveys/responses/${callId}/existing`),
  submit: (callId: number, data: { survey_id: number; radio_answers: { question_id: number; score: number }[]; comments: { question_id: number; text: string }[] }) =>
    api.post<SurveyResponse>(`/surveys/responses/${callId}`, data),
  update: (callId: number, data: { survey_id: number; radio_answers: { question_id: number; score: number }[]; comments: { question_id: number; text: string }[] }) =>
    api.patch<SurveyResponse>(`/surveys/responses/${callId}`, data),
  sendEmail: (data: { call_id: number; recipients: string[]; subject: string; body: string }) =>
    api.post<{ message: string }>("/surveys/send-email", data),
  callRecipients: (callId: number) =>
    api.get<{ recipients: string[] }>(`/surveys/call-recipients/${callId}`),
  sendTerminationEmail: (data: { call_id: number; recipients?: string[]; subject?: string; body?: string }) =>
    api.post<{ message: string }>("/surveys/send-termination-email", data),
  emailPreview: (callId: number, emailType: "review" | "stop") =>
    api.get<{
      recipient_fields: { label: string; email: string }[];
      recipients: string[];
      subject: string;
      body: string;
      score: number | null;
      wave_file_url: string;
      template_source: string;
    }>(`/surveys/email-preview/${callId}/${emailType}`),
  emailTemplateVariables: () =>
    api.get<{ name: string; description: string }[]>("/surveys/email-template-variables"),
  reviewRatings: (mcId?: number) =>
    api.get<any[]>(`/surveys/review-ratings${mcId ? `?mc_id=${mcId}` : ""}`),
  generatePdf: async (callId: number): Promise<Blob> => {
    const url = `${process.env.REACT_APP_API_URL || "/api"}/surveys/generate-pdf/${callId}`;
    const res = await fetch(url, { method: "POST", credentials: "include" });
    if (!res.ok) throw new Error("Failed to generate PDF");
    return res.blob();
  },
};

// === Reports ===
export const reports = {
  callsByShopper: (params?: string) =>
    api.get<ShopperCallSummary[]>(`/reports/calls-by-shopper${params ? `?${params}` : ""}`),
  callsByServiceLevel: (params?: string) =>
    api.get<ServiceLevelReport[]>(`/reports/calls-by-service-level${params ? `?${params}` : ""}`),
  callerFees: (params?: string) =>
    api.get<CallerFeeSummary[]>(`/reports/caller-fees${params ? `?${params}` : ""}`),
  callerFeesByType: (params?: string) =>
    api.get<CallerFeeByType[]>(`/reports/caller-fees-by-type${params ? `?${params}` : ""}`),
  exceptionalCalls: (params?: string) =>
    api.get<ExceptionalCallOut[]>(`/reports/exceptional-calls${params ? `?${params}` : ""}`),
  completedByDate: (startDate: string, endDate: string) =>
    api.get<{ date: string; count: number }[]>(
      `/reports/completed-calls-by-date?start_date=${startDate}&end_date=${endDate}`
    ),
  hmcMonthlySummary: (params?: string) =>
    api.get<HMCMonthlySummaryRow[]>(`/reports/hmc-monthly-summary${params ? `?${params}` : ""}`),
  scheduledVsCompleted: (params?: string) =>
    api.get<ScheduledVsCompletedRow[]>(`/reports/scheduled-vs-completed${params ? `?${params}` : ""}`),
  callStatusByMonth: (year: number, month?: number, mcId?: number) =>
    api.get<CallStatusByMonthRow[]>(`/reports/call-status-by-month?year=${year}${month ? `&month=${month}` : ""}${mcId ? `&mc_id=${mcId}` : ""}`),
  completedByHMCMonth: (year: number) =>
    api.get<CompletedByHMCMonthRow[]>(`/reports/completed-by-hmc-month?year=${year}`),
  completedByShopperMonth: (year: number, mcId?: number) =>
    api.get<CompletedByShopperMonthRow[]>(`/reports/completed-by-shopper-month?year=${year}${mcId ? `&mc_id=${mcId}` : ""}`),
  duplicateScheduledCalls: (year: number, month?: number, mcId?: number) =>
    api.get<DuplicateScheduledRow[]>(`/reports/duplicate-scheduled-calls?year=${year}${month ? `&month=${month}` : ""}${mcId ? `&mc_id=${mcId}` : ""}`),
  managementDashboard: (params?: string) =>
    api.get<ManagementDashboardOut>(`/reports/management-dashboard${params ? `?${params}` : ""}`),
  businessIntelligence: (params?: string) =>
    api.get<BusinessIntelligenceOut>(`/reports/business-intelligence${params ? `?${params}` : ""}`),
  // Excel exports — these return file downloads
  exportUrl: (report: string, params?: string) =>
    `${process.env.REACT_APP_API_URL || "/api"}/reports/export/${report}${params ? `?${params}` : ""}`,
};

// === Announcements ===
export const announcements = {
  list: () => api.get<Announcement[]>("/announcements/"),
  create: (data: { title: string; content: string; priority?: number }) =>
    api.post<Announcement>("/announcements/", data),
  update: (id: number, data: Partial<Announcement>) =>
    api.patch<Announcement>(`/announcements/${id}`, data),
  delete: (id: number) => api.delete<{ message: string }>(`/announcements/${id}`),
  variables: () => api.get<SystemVariable[]>("/announcements/variables"),
  getVariable: (id: number) => api.get<SystemVariable>(`/announcements/variables/${id}`),
  updateVariable: (id: number, data: { value?: string; description?: string }) =>
    api.patch<SystemVariable>(`/announcements/variables/${id}`, data),
  deleteVariable: (id: number) => api.delete<{ message: string }>(`/announcements/variables/${id}`),
};

// === Maintenance ===
export const maintenance = {
  getStatus: () => api.get<{ is_down: boolean; message: string }>("/maintenance/status"),
  setStatus: (isDown: boolean, message?: string) =>
    api.post<{ is_down: boolean; message: string }>("/maintenance/status", { is_down: isDown, message }),
};
